import { useState, useCallback } from "react";

export function useProductForm(categories) {
  const initialProductState = {
    name: "",
    categoryId: "",
    price: "",
    quantity: "",
    popular: false,
    description: "",
    specifications: {
      weight: "",
      colour: "",
      material: "",
      dimensions: "",
    },
    images: [],               // NEW uploaded files
    imagePreviews: [],        // UI previews
    existingImages: [],       // ✅ paths already on server

    video: null,              // NEW uploaded file
    videoPreview: null,
    existingVideo: null,
  };

  const [productsForm, setProductsForm] = useState([initialProductState]);

  const updateProductField = (index, field, value) => {
    setProductsForm((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateSpecificationField = (index, field, value) => {
    setProductsForm((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        specifications: {
          ...updated[index].specifications,
          [field]: value,
        },
      };
      return updated;
    });
  };

  const handleImageUpload = (index, files) => {
    if (files && files.length > 0) {
      const currentImages = productsForm[index].imagePreviews;
      const remaining = 3 - currentImages.length;
      if (remaining <= 0) return;

      const newFiles = Array.from(files).slice(0, remaining);

      newFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setProductsForm((prev) => {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              imagePreviews: [...updated[index].imagePreviews, reader.result],
              images: [...updated[index].images, file],
            };
            return updated;
          });
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveImage = (productIndex, imageIndex) => {
    setProductsForm((prev) => {
      const updated = [...prev];
      updated[productIndex] = {
        ...updated[productIndex],
        imagePreviews: updated[productIndex].imagePreviews.filter((_, i) => i !== imageIndex),
        images: updated[productIndex].images.filter((_, i) => i !== imageIndex),
        existingImages: updated[productIndex].existingImages.filter((_, i) => i !== imageIndex),
      };
      return updated;
    });
  };

  const handleVideoUpload = (index, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductsForm((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            video: file,
            videoPreview: reader.result,
          };
          return updated;
        });
      };
      reader.readAsDataURL(file);
    }
  };


  const handleRemoveVideo = (index) => {
    setProductsForm((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        video: null,          // new upload removed
        videoPreview: null,   // UI cleared
        existingVideo: null,  // tells backend to delete existing video
      };
      return updated;
    });
  };

  const addAnotherProduct = () => {
    setProductsForm((prev) => {
      const newProduct = { ...initialProductState };
      return [...prev, newProduct];
    });
  };

  const removeProductForm = (index) => {
    if (productsForm.length > 1) {
      setProductsForm((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const resetForm = useCallback((initialData = null) => {
    if (initialData) {
      // Map existing product data to form structure
      setProductsForm([
        {
          ...initialProductState,
          ...initialData,
          categoryId: initialData.categoryId.toString(),
          imagePreviews: initialData.images || [],
          existingImages: initialData.images || [],

          videoPreview: initialData.video || null,
          existingVideo: initialData.video || null,
          specifications:
            initialData.specifications || initialProductState.specifications,
        },
      ]);
    } else {
      setProductsForm([initialProductState]);
    }
  }, []);

  const getValidProducts = () => {
    return productsForm.filter((p) => {
      return (
        p.name.trim() &&
        p.categoryId &&
        p.price &&
        p.images.length >= 1 // 🔥 REQUIRED
      );
    });
  };


  const buildFormDataForCreate = (products) => {
    const formData = new FormData();

    // Build clean JSON payload (NO FILES)
    const payload = products.map((p) => ({
      name: p.name,
      price: p.price,
      quantity: p.quantity,
      category_id: p.categoryId,
      popular: p.popular,
      description: p.description,
      specifications: p.specifications,
    }));

    // Attach JSON payload
    formData.append("data", JSON.stringify(payload));

    // Attach files in backend-expected format
    products.forEach((product, index) => {
      // Append multiple images with "[]" to indicate array
      product.images.forEach((file) => {
        formData.append(`images_${index}[]`, file);
      });

      if (product.video) {
        formData.append(`video_${index}`, product.video);
      }
    });

    return formData;
  };

  const buildFormDataForUpdate = (product) => {
    const formData = new FormData();

    // JSON payload
    const payload = {
      name: product.name,
      price: product.price,
      quantity: product.quantity,
      category_id: product.categoryId,
      popular: product.popular,
      description: product.description,
      specifications: product.specifications,

      // ✅ explicitly tell backend what media still exists
      existing_images: product.existingImages,
      existing_video: product.existingVideo,
    };

    formData.append("data", JSON.stringify(payload));

    // Append ONLY new images
    product.images.forEach((file) => {
      formData.append("images[]", file);
    });

    // Append new video only if replaced
    if (product.video) {
      formData.append("video", product.video);
    }

    return formData;
  };


  return {
    productsForm,
    updateProductField,
    updateSpecificationField,
    handleImageUpload,
    handleRemoveImage,
    handleVideoUpload,
    handleRemoveVideo,
    addAnotherProduct,
    removeProductForm,
    resetForm,
    getValidProducts,
    buildFormDataForCreate,
    buildFormDataForUpdate,
  };
}
